import { Tabs, TabsContent, TabsList, TabsTrigger } from "@aio/platform-ui";
import * as React from "react";

export interface DetailTab {
  id: string;
  /** Tab trigger label. ReactNode so callers can pass `<TranslatedText />`. */
  label: React.ReactNode;
  content: React.ReactNode;
}

export interface DetailPageProps {
  /**
   * Page title. Accepts a plain string OR a `ReactNode` so callers can pass
   * an i18n-resolving wrapper (e.g. `<TranslatedText id="..." />`) without
   * forcing the page-factory to do server-side translation lookups.
   */
  title: React.ReactNode;
  description?: React.ReactNode;
  status?: React.ReactNode;
  actions?: React.ReactNode;
  tabs: readonly DetailTab[];
  defaultTab?: string;
  /** Right-rail content (history/comments/files) */
  sidePanel?: React.ReactNode;
}

export function DetailPage({
  title,
  description,
  status,
  actions,
  tabs,
  defaultTab,
  sidePanel,
}: DetailPageProps) {
  const initialTab = defaultTab ?? tabs[0]?.id;
  const tabsProps = initialTab ? { defaultValue: initialTab } : {};
  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {status}
          </div>
          {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
        </div>
        {actions}
      </header>
      <div className={sidePanel ? "grid gap-6 lg:grid-cols-[1fr,20rem]" : ""}>
        <Tabs {...tabsProps}>
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              {t.content}
            </TabsContent>
          ))}
        </Tabs>
        {sidePanel ? <aside className="space-y-4">{sidePanel}</aside> : null}
      </div>
    </div>
  );
}
